/** GDPR / ePrivacy — süti és helyi tároló hozzájárulás (v1). */

export const CONSENT_STORAGE_KEY = "autopark_cookie_consent";
export const CONSENT_COOKIE_NAME = "autopark_cookie_consent";
export const CONSENT_SESSION_MIRROR = "autopark_cookie_consent_session";
export const CONSENT_VERSION = 1;

export const CONSENT_CATEGORIES = ["necessary", "functional", "statistics", "marketing"];

const NECESSARY_ONLY = {
  version: CONSENT_VERSION,
  necessary: true,
  functional: false,
  statistics: false,
  marketing: false,
  decidedAt: null,
};

function consentCookieDomain() {
  if (typeof window === "undefined") return "";
  const h = window.location.hostname.toLowerCase();
  if (h === "autoparks.hu" || h.endsWith(".autoparks.hu")) return "; Domain=.autoparks.hu";
  return "";
}

function readConsentCookieRaw() {
  if (typeof document === "undefined") return null;
  try {
    const escaped = CONSENT_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const m = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    if (!m) return null;
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

function writeConsentCookie(json) {
  if (typeof document === "undefined") return;
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${CONSENT_COOKIE_NAME}=${encodeURIComponent(json)}; Path=/; Max-Age=31536000; SameSite=Lax${secure}${consentCookieDomain()}`;
  } catch {
    /* ignore */
  }
}

function normalizeConsent(parsed) {
  if (!parsed || parsed.version !== CONSENT_VERSION) return null;
  const decidedAt = parsed.decidedAt || null;
  if (!decidedAt) return null;
  return {
    version: CONSENT_VERSION,
    necessary: true,
    functional: !!parsed.functional,
    statistics: !!parsed.statistics,
    marketing: !!parsed.marketing,
    decidedAt,
  };
}

function parseConsentJson(raw) {
  if (!raw) return null;
  try {
    return normalizeConsent(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Minden tárolóba — apex/www és OAuth után is ugyanaz a döntés. */
export function persistConsentRecord(record) {
  const normalized = normalizeConsent(record);
  if (!normalized) return null;
  const json = JSON.stringify(normalized);
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, json);
  } catch {
    /* ignore */
  }
  writeConsentCookie(json);
  try {
    sessionStorage.setItem(CONSENT_SESSION_MIRROR, json);
  } catch {
    /* ignore */
  }
  return normalized;
}

export function defaultConsent() {
  return { ...NECESSARY_ONLY };
}

export function readConsent() {
  const candidates = [];

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    const n = parseConsentJson(raw);
    if (n) candidates.push(n);
  } catch {
    /* ignore */
  }

  try {
    const n = parseConsentJson(readConsentCookieRaw());
    if (n) candidates.push(n);
  } catch {
    /* ignore */
  }

  try {
    const raw = sessionStorage.getItem(CONSENT_SESSION_MIRROR);
    const n = parseConsentJson(raw);
    if (n) candidates.push(n);
  } catch {
    /* ignore */
  }

  if (candidates.length === 0) return defaultConsent();

  candidates.sort((a, b) => String(b.decidedAt).localeCompare(String(a.decidedAt)));
  const best = candidates[0];
  persistConsentRecord(best);
  return best;
}

export function hasConsentDecision(consent = readConsent()) {
  return Boolean(consent?.decidedAt);
}

export function writeConsent(partial) {
  const prev = readConsent();
  const base = hasConsentDecision(prev) ? prev : defaultConsent();
  const next = {
    ...base,
    ...partial,
    version: CONSENT_VERSION,
    necessary: true,
    decidedAt: new Date().toISOString(),
  };
  return persistConsentRecord(next) || next;
}

export function allowsCategory(category, consent = readConsent()) {
  if (category === "necessary") return true;
  if (!hasConsentDecision(consent)) return false;
  return Boolean(consent[category]);
}

export function allowsFunctionalStorage(consent = readConsent()) {
  return allowsCategory("functional", consent);
}

export function allowsStatistics(consent = readConsent()) {
  return allowsCategory("statistics", consent);
}

export function acceptAllConsent() {
  return writeConsent({ functional: true, statistics: true, marketing: true });
}

export function rejectNonEssentialConsent() {
  return writeConsent({ functional: false, statistics: false, marketing: false });
}
